import { DownloadOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons"
import { App, Button, Flex, Input, Select, Spin, Typography } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { EntityTable } from "../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../components/EntityTable/surfaces.ts"
import { DateRangeFilter } from "../../components/Filters/DateRangeFilter.tsx"
import { FilePreviewModal } from "../../components/FilePreviewModal/FilePreviewModal.tsx"
import { TableResultCount } from "../../components/Table/TableResultCount.tsx"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { useScrolledTable } from "../../hooks/use-scrolled-table.ts"
import { useTableContentHeight } from "../../hooks/use-table-content-height.ts"
import { nextPageUrl } from "../../redux/shared/paged-list.ts"
import { downloadBlob } from "../../utils/download-file.ts"
import { extractParamsFromNextUrl } from "../../utils/form/extract-params-from-next-url.ts"
import { showNotification } from "../../utils/notification/show-notification.ts"
import { getCustomers } from "../CustomersPage/api/services.ts"
import { IssueInvoiceDrawer } from "../Project/ProjectViewPage/components/sections/ProjectViewFinance/ProjectViewFinance/IssueInvoiceDrawer.tsx"
import { INVOICE_STATUSES_MAP } from "../Project/ProjectViewPage/constants/invoice-statuses.ts"
import type { InvoicesListParams } from "./api/invoices.services.ts"
import { ChangeInvoiceStatusModal } from "./components/ChangeInvoiceStatusModal.tsx"
import { InvoiceDeliveryHistoryModal } from "./components/InvoiceDeliveryHistoryModal.tsx"
import { InvoicesEmptyState } from "./components/InvoicesEmptyState.tsx"
import { InvoicesKpiRow } from "./components/InvoicesKpiRow.tsx"
import { InvoicesMoreFilters } from "./components/InvoicesMoreFilters.tsx"
import { SendInvoiceConfirmModal } from "./components/SendInvoiceConfirmModal.tsx"
import { type InvoiceRowAction, getColumns } from "./components/columns.tsx"
import { handleInvoiceApiError } from "./helpers/handle-invoice-api-error.ts"
import { useInvoiceDocumentPreview } from "./hooks/use-invoice-document-preview.ts"
import {
  invoicesResetFilters,
  invoicesSetFilters,
  invoicesSetOrdering
} from "./redux/invoices.slice.ts"
import {
  deleteInvoice,
  downloadInvoicePdf,
  exportInvoicesList,
  fetchInvoicesList,
  fetchInvoicesSummary,
  issueInvoice,
  patchGeneratedInvoice,
  sendInvoice,
  voidInvoice
} from "./redux/invoices.thunks.ts"
import { INVOICE_STATUSES, type InvoiceListItem } from "./types/invoices.types.ts"

const { Title, Text } = Typography

const InvoicesPage = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { modal, notification } = App.useApp()
  const [fetchFailed, setFetchFailed] = useState(false)
  const [configOpen, setConfigOpen] = useState(false)
  const [sendTarget, setSendTarget] = useState<InvoiceListItem | null>(null)
  const [sending, setSending] = useState(false)
  const [statusTarget, setStatusTarget] = useState<InvoiceListItem | null>(null)
  const [changingStatus, setChangingStatus] = useState(false)
  const [deliveryTarget, setDeliveryTarget] = useState<InvoiceListItem | null>(
    null
  )

  const items = useAppSelector((state) => state.invoices.items)
  const count = useAppSelector((state) => state.invoices.count)
  const next = useAppSelector((state) => state.invoices.next)
  const retryNext = useAppSelector((state) => state.invoices.retryNext)
  const fetching = useAppSelector((state) => state.invoices.fetching)
  const exporting = useAppSelector((state) => state.invoices.exporting)
  const filters = useAppSelector((state) => state.invoices.filters)
  const ordering = useAppSelector((state) => state.invoices.ordering)
  const summary = useAppSelector((state) => state.invoices.summary)
  const summaryFetching = useAppSelector(
    (state) => state.invoices.summaryFetching
  )
  const summaryFailed = useAppSelector((state) => state.invoices.summaryFailed)
  const permissions = useAppSelector((state) => state.auth.permissions)
  const userLabels = useAppSelector((state) => state.auth.permissions?.labels)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingInvoice, setEditingInvoice] = useState<InvoiceListItem | null>(
    null
  )
  const [customerOptions, setCustomerOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [customerSearching, setCustomerSearching] = useState(false)
  const [searchTerm, setSearchTerm] = useState(filters.q)
  const customerSearchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  const searchCustomers = useCallback((term: string) => {
    clearTimeout(customerSearchTimer.current)
    if (term.length < 3) return
    customerSearchTimer.current = setTimeout(async () => {
      setCustomerSearching(true)
      const response = await getCustomers({ q: term })
      setCustomerSearching(false)
      if (response.ok && response.data) {
        setCustomerOptions(
          response.data.results.map((customer) => ({
            value: customer.id,
            label:
              [customer.first_name, customer.last_name]
                .filter(Boolean)
                .join(" ") || customer.email
          }))
        )
      }
    }, 300)
  }, [])

  const editAllowed = Boolean(
    permissions?.codes?.projects?.invoices?.project_invoice_edit
  )
  // Void requires the GCC owner or admin label (enforced server-side too).
  const voidAllowed = Boolean(
    userLabels?.some(
      (label) => label.code === "gcc" || label.code === "gcc_admin"
    )
  )

  const handleLoadMore = useCallback(() => {
    const url = nextPageUrl({ next, retryNext })
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    dispatch(
      fetchInvoicesList({
        ...extractParamsFromNextUrl<InvoicesListParams>(url),
        isMore: true
      })
    )
  }, [next, retryNext, dispatch])

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: fetching,
    next: next,
    loadMore: handleLoadMore,
    fullPage: true
  })

  // The fixed Actions column forces AntD's synced-scroll body into a real
  // height, so a short result set renders the whole viewport-height box with a
  // blank rectangle under its rows. Measured rather than guessed from a row
  // count — see the height rule in `docs/kb`.
  const contentHeight = useTableContentHeight(tableRef)
  const effectiveTableHeight = contentHeight
    ? Math.min(tableHeight, contentHeight)
    : tableHeight

  const buildFilterParams = useCallback(() => {
    return {
      ...(filters.q ? { q: filters.q } : {}),
      ...(filters.customer ? { customer: filters.customer } : {}),
      ...(filters.origin ? { origin: filters.origin } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.date_issued_from
        ? { date_issued_from: filters.date_issued_from }
        : {}),
      ...(filters.date_issued_to
        ? { date_issued_to: filters.date_issued_to }
        : {}),
      ...(filters.due_date_from
        ? { due_date_from: filters.due_date_from }
        : {}),
      ...(filters.due_date_to ? { due_date_to: filters.due_date_to } : {})
    }
  }, [filters])

  const buildParams = useCallback(() => {
    return {
      page: filters.page,
      page_size: filters.page_size,
      ...buildFilterParams(),
      ...(ordering ? { ordering } : {})
    }
  }, [filters, ordering, buildFilterParams])

  const updateData = useCallback(() => {
    dispatch(fetchInvoicesList(buildParams()))
      .unwrap()
      .then(() => setFetchFailed(false))
      .catch(() => setFetchFailed(true))
    dispatch(fetchInvoicesSummary(buildFilterParams()))
  }, [dispatch, buildParams, buildFilterParams])

  useEffect(() => {
    generateDocumentTitle(t("invoices.title"))
  }, [t])

  useEffect(() => {
    setSearchTerm(filters.q)
  }, [filters.q])

  useEffect(() => {
    updateData()
  }, [updateData])

  const handleExport = async () => {
    const blob = await dispatch(exportInvoicesList(buildParams())).unwrap()
    downloadBlob(blob, "invoices.xlsx")
  }

  const translate = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      t(key, options) as string,
    [t]
  )

  const openNewInvoice = () => {
    setEditingInvoice(null)
    setDrawerOpen(true)
  }

  const reportError = useCallback(
    (payload: unknown) =>
      handleInvoiceApiError({ payload, modal, t: translate, navigate }),
    [modal, translate, navigate]
  )

  // A blob response carries no readable status, so one generic message has to cover every failure.
  const reportPreviewError = useCallback(
    () => showNotification.error({ message: t("invoices.previewFailed") }),
    [t]
  )

  const preview = useInvoiceDocumentPreview(reportPreviewError)

  const handleSelectRow = async (
    record: InvoiceListItem,
    action: InvoiceRowAction
  ) => {
    switch (action) {
      case "edit":
        setEditingInvoice(record)
        setDrawerOpen(true)
        return
      case "issue":
        try {
          await dispatch(issueInvoice(record.id)).unwrap()
          updateData()
        } catch (payload) {
          reportError(payload)
        }
        return
      case "delete":
        modal.confirm({
          title: t("invoices.deleteConfirm.title"),
          content: t("invoices.deleteConfirm.body"),
          okText: t("invoices.deleteConfirm.ok"),
          okButtonProps: { danger: true },
          cancelText: t("invoices.deleteConfirm.cancel"),
          onOk: async () => {
            try {
              await dispatch(deleteInvoice(record.id)).unwrap()
              updateData()
            } catch (payload) {
              reportError(payload)
            }
          }
        })
        return
      case "void":
        modal.confirm({
          title: t("invoices.voidConfirm.title", {
            number: record.invoice_number
          }),
          content: t("invoices.voidConfirm.body"),
          okText: t("invoices.voidConfirm.ok"),
          okButtonProps: { danger: true },
          cancelText: t("invoices.voidConfirm.cancel"),
          onOk: async () => {
            try {
              await dispatch(voidInvoice(record.id)).unwrap()
              updateData()
            } catch (payload) {
              reportError(payload)
            }
          }
        })
        return
      case "send":
        setSendTarget(record)
        return
      case "change_status":
        setStatusTarget(record)
        return
      case "delivery_history":
        setDeliveryTarget(record)
        return
      case "preview":
        await preview.open(record)
        return
      case "download": {
        const blob = await dispatch(downloadInvoicePdf(record.id)).unwrap()
        downloadBlob(blob, `${record.invoice_number || "invoice"}.pdf`)
      }
    }
  }

  const handleConfirmSend = async (recipient: string) => {
    if (!sendTarget) return
    setSending(true)
    try {
      await dispatch(sendInvoice({ id: sendTarget.id, recipient })).unwrap()
      notification.success({ message: t("invoices.sent") })
      setSendTarget(null)
      updateData()
    } catch (payload) {
      reportError(payload)
    } finally {
      setSending(false)
    }
  }

  const handleConfirmStatus = async (status: number) => {
    if (!statusTarget) return
    setChangingStatus(true)
    try {
      await dispatch(
        patchGeneratedInvoice({ id: statusTarget.id, status })
      ).unwrap()
      notification.success({ message: t("invoices.changeStatus.success") })
      setStatusTarget(null)
      updateData()
    } catch (payload) {
      reportError(payload)
    } finally {
      setChangingStatus(false)
    }
  }

  const columns = useMemo(
    () =>
      getColumns({
        t: translate,
        editAllowed,
        voidAllowed,
        onSelectRow: handleSelectRow
      }),
    [translate, editAllowed, voidAllowed, filters, ordering]
  )

  const statusOptions = Object.values(INVOICE_STATUSES_MAP)
    .filter((status) => status.value !== INVOICE_STATUSES.PENDING)
    .map((status) => ({
      value: status.value,
      label: status.label
    }))

  const hasActiveFilters = Boolean(
    filters.q ||
      filters.customer ||
      filters.origin ||
      filters.status ||
      filters.date_issued_from ||
      filters.date_issued_to ||
      filters.due_date_from ||
      filters.due_date_to
  )

  const clearFilters = () => {
    dispatch(invoicesResetFilters())
  }

  const handleFilterChange = useCallback(
    (patch: Partial<typeof filters>) => {
      dispatch(invoicesSetFilters({ ...patch, page: 1 }))
    },
    [dispatch]
  )

  return (
    <>
      <Flex vertical gap={16}>
        <Flex justify="space-between" align="start" wrap gap={8}>
          <Flex vertical gap={2}>
            {/* app.less sets a global `h1,h2,h3{text-align:center}` reset —
                Title renders as an h3, so it needs an explicit override here
                (a class beats that element selector on specificity alone). */}
            <Title level={3} className="m-0 text-left text-[21px] font-semibold">
              {t("invoices.title")}
            </Title>
            <Text type="secondary" className="text-xs">
              {t("invoices.subtitle")}
            </Text>
          </Flex>
          <Flex gap={8} wrap>
            <Button
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={handleExport}
            >
              {t("invoices.export")}
            </Button>
            {editAllowed && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openNewInvoice}
              >
                {t("invoices.newInvoice")}
              </Button>
            )}
          </Flex>
        </Flex>

        <InvoicesKpiRow
          summary={summary}
          loading={summaryFetching}
          failed={summaryFailed}
        />

        <Flex
          wrap="wrap"
          gap="small"
          justify="space-between"
          align="center"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2.5"
        >
          <Flex wrap="wrap" gap="small" align="center" className="min-w-0 flex-1">
            <Input.Search
              allowClear
              placeholder={t("invoices.searchPlaceholder")}
              className="min-w-60 flex-1"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              onSearch={(value) => handleFilterChange({ q: value })}
            />
            <Select
              allowClear
              aria-label={t("invoices.columns.status")}
              placeholder={t("invoices.columns.status")}
              style={{ width: 170 }}
              options={statusOptions}
              value={filters.status ?? undefined}
              onChange={(value) =>
                handleFilterChange({ status: value ?? null })
              }
            />
            <Select
              allowClear
              showSearch
              filterOption={false}
              aria-label={t("invoices.filters.customer")}
              placeholder={t("invoices.filters.customer")}
              style={{ width: 200 }}
              options={customerOptions}
              notFoundContent={customerSearching ? <Spin size="small" /> : null}
              value={filters.customer ?? undefined}
              onSearch={searchCustomers}
              onChange={(value) =>
                handleFilterChange({ customer: value ?? null })
              }
            />
            <DateRangeFilter
              after={filters.due_date_from}
              before={filters.due_date_to}
              afterKey="due_date_from"
              beforeKey="due_date_to"
              className="w-48"
              placeholder={t("invoices.filters.dueDate")}
              onChange={handleFilterChange}
            />
            <InvoicesMoreFilters
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          </Flex>
          <Flex gap="small" align="center">
            {hasActiveFilters && (
              <Button type="link" onClick={clearFilters}>
                {t("invoices.clearFilters")}
              </Button>
            )}
            <Button
              icon={<SettingOutlined />}
              onClick={() => setConfigOpen(true)}
            >
              {t("entityTable.customize")}
            </Button>
          </Flex>
        </Flex>

        <div
          ref={tableRef}
          className="overflow-hidden rounded-lg border border-gray-200 bg-white"
        >
          <EntityTable<InvoiceListItem>
            surface={TABLE_SURFACES.invoices}
            selectionMode="on-demand"
            selectionFilters={filters}
            tab={DEFAULT_TAB}
            columns={columns}
            dataSource={items}
            loading={fetching && !items?.length}
            rowKey="id"
            hideConfigButton
            configControl={{ open: configOpen, onOpenChange: setConfigOpen }}
            locale={{
              emptyText: fetchFailed ? (
                <InvoicesEmptyState variant="error" onRetry={updateData} />
              ) : hasActiveFilters ? (
                <InvoicesEmptyState
                  variant="filtered"
                  onClearFilters={clearFilters}
                />
              ) : (
                <InvoicesEmptyState
                  variant="none"
                  canCreate={editAllowed}
                  onCreateInvoice={openNewInvoice}
                />
              )
            }}
            onScroll={onTableScroll}
            withOutPagination
            pagination={false}
            onChange={(_pagination, _filters, sorter) => {
              const sorterValue = Array.isArray(sorter) ? sorter[0] : sorter
              const nextOrdering = sorterValue?.order
                ? `${sorterValue.order === "descend" ? "-" : ""}${String(
                    sorterValue.field
                  )}`
                : null
              if (nextOrdering !== ordering) {
                dispatch(invoicesSetOrdering(nextOrdering))
              }
            }}
            scroll={{ x: 1280, y: effectiveTableHeight }}
          />
          <div className="border-t border-gray-200">
            <TableResultCount
              loaded={items?.length ?? 0}
              total={count}
              loading={fetching}
              next={next}
              retryNext={retryNext}
              onRetry={handleLoadMore}
            />
          </div>
        </div>
      </Flex>

      <IssueInvoiceDrawer
        open={drawerOpen}
        invoice={editingInvoice ?? undefined}
        onClose={() => {
          setDrawerOpen(false)
          setEditingInvoice(null)
        }}
        onSaved={() => {
          setDrawerOpen(false)
          setEditingInvoice(null)
          updateData()
        }}
      />

      <SendInvoiceConfirmModal
        open={Boolean(sendTarget)}
        defaultRecipient={sendTarget?.customer_email}
        loading={sending}
        onConfirm={handleConfirmSend}
        onCancel={() => setSendTarget(null)}
      />

      <ChangeInvoiceStatusModal
        open={Boolean(statusTarget)}
        currentStatus={statusTarget?.status}
        loading={changingStatus}
        onConfirm={handleConfirmStatus}
        onCancel={() => setStatusTarget(null)}
      />

      <InvoiceDeliveryHistoryModal
        open={Boolean(deliveryTarget)}
        invoiceId={deliveryTarget?.id}
        onClose={() => setDeliveryTarget(null)}
      />

      {preview.loading && <Spin fullscreen />}
      <FilePreviewModal
        visible={Boolean(preview.doc)}
        doc={preview.doc}
        onClose={preview.close}
      />
    </>
  )
}

export default InvoicesPage
